/**
 * web/src/screens/Notebook/context.tsx — the desktop context panel, the third
 * zone of the shell (card vncVrGk1hc9i: NotebookContext moved out of
 * index.tsx unchanged; the parent still owns every action via props).
 */
import { useState } from 'react';
import { api } from '../../shell/bridge';
import { type Member } from '../../lib/api';
import { Pressable } from '../../ui/Pressable';
import {
  type NotebookItem, type Suggestion, type PlacePick,
  tagText, hostOf, placeFromPick, bylineAuthor, bylineLabel,
  doneVerb, INTENT_VERB, ENTRY_LABEL,
} from './types';
import { ENTRY_ICON, PLACE_PIN } from './icons';

/* ── Context panel (card 2wn9seit8zGi, PRD §6.4) ──────────────────────────
   The third zone of the desktop shell. P1: selecting a card opens it here and
   Esc closes it. P2: who saved it, when, what it is, who can see it, and the
   source. P4: the next steps that actually exist for this row. P5: with
   nothing selected it is NEVER an empty box — "Concierge suggests" (the scan
   results already waiting on the tags endpoint) over "Recently saved".

   It renders only at >=760px (PRD L5) and is a real landmark, not a tooltip:
   the feed stays where it is, the panel scrolls on its own, and one item is
   open at a time by construction — `contextId` is a single string. */
export function NotebookContext({ item, suggestions, recent, members, meId, busy, onClose, onOpen, onDone, onRemove, onEdit, onPlaceSave }: {
  item: NotebookItem | null;
  suggestions: Suggestion[];
  recent: NotebookItem[];
  members: Member[];
  meId: string | null;
  busy: boolean;
  onClose: () => void;
  onOpen: (itemId: string) => void;
  onDone: (item: NotebookItem) => void;
  onRemove: (item: NotebookItem) => void;
  onEdit: (item: NotebookItem) => void;
  /** PRD E2 — the correction half of the panel's entity chip. The parent owns
   *  the reload, so the panel never mutates the feed's own copy. */
  onPlaceSave: (item: NotebookItem, place: string) => Promise<void>;
}) {
  /* PRD P2 asks for an editable title and notes. The panel is the read face
     and the editor is the card's own inline form (one editor, one source of
     truth) — "Edit" hands back to the feed rather than growing a second copy
     of the same PUT. */
  const isOwner = item !== null && meId !== null && item.addedBy === meId;
  const title = item
    ? (item.entryType === 'link' ? (item.title?.trim() || hostOf(item.url)) : (item.body?.trim() || ENTRY_LABEL[item.entryType]))
    : '';

  /* PRD E1/E2 — the entity chip on the panel, and the correction behind it.
     The resolution itself runs on the card's own places route (the dev mock
     answers here, so no spend), and the save goes through the parent so the
     feed re-reads its own rows instead of this panel editing a copy. */
  const [correcting, setCorrecting] = useState(false);
  const [correctText, setCorrectText] = useState('');
  const [correctSearchBusy, setCorrectSearchBusy] = useState(false);
  const [correctBusy, setCorrectBusy] = useState(false);
  const [correctNote, setCorrectNote] = useState<string | null>(null);
  const [correctResults, setCorrectResults] = useState<PlacePick[]>([]);
  const [correctError, setCorrectError] = useState<string | null>(null);

  const openCorrect = () => {
    if (!item) return;
    setCorrectText(item.place ?? '');
    setCorrectResults([]);
    setCorrectNote(null);
    setCorrectError(null);
    setCorrecting(true);
  };

  const closeCorrect = () => {
    setCorrecting(false);
    setCorrectResults([]);
    setCorrectNote(null);
    setCorrectError(null);
  };

  /** One search, same route and same plain-words fallbacks the card's own
   *  detail form uses — two spellings of that message would drift. */
  const searchCorrect = async () => {
    const text = correctText.trim();
    if (!text || correctSearchBusy) return;
    setCorrectSearchBusy(true);
    setCorrectNote(null);
    try {
      const res = await api<{ places: PlacePick[]; capped: boolean; reason: string | null }>(
        '/api/notebook/places',
        { method: 'POST', body: { text } },
      );
      setCorrectResults(res.places ?? []);
      if (res.capped) {
        setCorrectNote("That's today's location search limit — the plain text box still works.");
      } else if ((res.places ?? []).length === 0) {
        setCorrectNote("Nothing came back for that — type the place in yourself and it'll save fine.");
      }
    } catch (err) {
      console.error('[Pear] panel place search failed', err);
      setCorrectNote("Couldn't search just now — type the place in yourself.");
      setCorrectResults([]);
    } finally {
      setCorrectSearchBusy(false);
    }
  };

  const saveCorrect = async (target: NotebookItem) => {
    if (correctBusy) return;
    setCorrectBusy(true);
    setCorrectError(null);
    try {
      await onPlaceSave(target, correctText.trim());
      closeCorrect();
    } catch (err) {
      console.error('[Pear] panel place save failed', err);
      setCorrectError("That correction didn't save — what's on the card is unchanged. Try again.");
    } finally {
      setCorrectBusy(false);
    }
  };

  return (
    <aside className="nb-context" id="nb-context" aria-label="Card details" data-testid="nb-context">
      {item === null ? (
        /* P5 — the never-empty default. */
        <div className="nb-context-idle">
          <section className="nb-context-mod" aria-labelledby="nb-ctx-suggest-head">
            <h3 className="nb-context-head" id="nb-ctx-suggest-head">Concierge suggests</h3>
            {suggestions.length === 0 ? (
              <p className="nb-context-empty">
                Nothing to suggest yet — save a link and I'll read it while you work.
              </p>
            ) : (
              <ul className="nb-context-list">
                {suggestions.slice(0, 3).map(s => (
                  <li key={s.id}>
                    <button type="button" className="nb-context-row" onClick={() => onOpen(s.itemId)}>
                      <span className="nb-context-row-title">{s.subject || tagText(s.kind, s.tag)}</span>
                      <span className="nb-context-row-why">{s.rationale}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="nb-context-mod" aria-labelledby="nb-ctx-recent-head">
            <h3 className="nb-context-head" id="nb-ctx-recent-head">Recently saved</h3>
            {recent.length === 0 ? (
              <p className="nb-context-empty">Your first card will show up here.</p>
            ) : (
              <ul className="nb-context-list">
                {recent.slice(0, 4).map(r => (
                  <li key={r.itemId}>
                    <button type="button" className="nb-context-row" onClick={() => onOpen(r.itemId)}>
                      <span className="nb-context-row-title">
                        {r.entryType === 'link' ? (r.title?.trim() || hostOf(r.url)) : (r.body?.trim() || ENTRY_LABEL[r.entryType])}
                      </span>
                      <span className="nb-context-row-why">{bylineLabel(r.createdAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : (
        <div className="nb-context-item" data-testid="nb-context-item">
          <div className="nb-context-top">
            <span className={`nb-cat nb-cat-${item.entryType}`}>
              <span className="nb-icon" aria-hidden="true">{ENTRY_ICON[item.entryType]}</span>
              {ENTRY_LABEL[item.entryType]}
            </span>
            <button type="button" className="nb-context-close" data-test="nb-context-close" onClick={onClose}>
              Close
            </button>
          </div>

          <h3 className="nb-context-title">{title}</h3>

          <p className="nb-context-meta">
            By {bylineAuthor(item.addedBy, members, meId)}, {bylineLabel(item.createdAt)}
            {' · '}
            <span className={item.shared ? 'nb-context-vis-shared' : 'nb-context-vis-private'} data-testid="nb-context-visibility">
              {item.shared ? 'Shared' : 'Just you'}
            </span>
          </p>

          {/* P2's source line — a link card keeps the door it came through. */}
          {item.entryType === 'link' && (
            <a className="nb-context-source" href={item.url} target="_blank" rel="noreferrer noopener" data-test="nb-context-source">
              {hostOf(item.url)} ↗
            </a>
          )}

          {item.description && <p className="nb-context-note">{item.description}</p>}

          {/* PRD E1/E2 — the resolved place rides beside the words it came
              from, as a chip the eye can pick out of the panel. A card with
              no place renders no chip: unresolved text shows exactly as it
              was typed, and the correction door is owner-only (same gate as
              Edit, because it writes). */}
          {(item.place || isOwner) && (
            <div className="nb-ctx-entities" data-test="nb-ctx-entity-chips">
              {item.place && (
                <span className="nb-entity-chip" data-entity="place" data-testid="nb-context-place">
                  {PLACE_PIN}
                  {item.place}
                </span>
              )}
              {isOwner && !correcting && (
                <button type="button" className="nb-entity-fix" data-test="nb-correct" onClick={openCorrect}>
                  {item.place ? 'Correct' : 'Add a place'}
                </button>
              )}
            </div>
          )}

          {/* The correction itself — search against the card's own places
              route (the dev mock answers here) or type the name yourself;
              either way the save is one PUT on the card's edit verb. */}
          {correcting && item && (
            <div className="nb-correct-form" data-test="nb-correct-form">
              <label className="nb-label" htmlFor="nb-correct-place">Where?</label>
              <div className="nb-place-row">
                <input
                  id="nb-correct-place"
                  className="pear-chat-input nb-input"
                  data-test="nb-correct-place"
                  placeholder="Search for a place, or type it in"
                  value={correctText}
                  onChange={e => setCorrectText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void searchCorrect();
                    }
                  }}
                />
                <Pressable
                  className="pear-btn nb-place-search"
                  data-test="nb-correct-search"
                  disabled={correctSearchBusy || !correctText.trim()}
                  onClick={() => void searchCorrect()}
                >
                  {correctSearchBusy ? 'Searching…' : 'Search'}
                </Pressable>
              </div>
              {correctNote && <p className="nb-hint" data-test="nb-correct-note">{correctNote}</p>}
              {correctResults.length > 0 && (
                <ul className="nb-place-results" data-test="nb-correct-results">
                  {correctResults.map(p => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="nb-place-result"
                        data-test="nb-correct-result"
                        onClick={() => {
                          setCorrectText(placeFromPick(p));
                          setCorrectResults([]);
                          setCorrectNote(null);
                        }}
                      >
                        <span className="nb-place-name">{p.name}</span>
                        {p.address && <span className="nb-place-addr">{p.address}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {correctError && (
                <p className="nb-save-error" data-test="nb-correct-error" role="alert">{correctError}</p>
              )}
              <div className="nb-composer-actions">
                <Pressable className="pear-btn" data-test="nb-correct-cancel" onClick={closeCorrect}>
                  Cancel
                </Pressable>
                <Pressable
                  className="pear-btn pear-btn-mustard"
                  data-test="nb-correct-save"
                  disabled={correctBusy}
                  onClick={() => void saveCorrect(item)}
                >
                  {correctBusy ? 'Saving…' : 'Save'}
                </Pressable>
              </div>
            </div>
          )}

          {/* P4 — next steps that actually do something. No speculative
              concierge verbs: every control here hits an endpoint the card
              already owns. */}
          <div className="nb-context-steps" aria-label="Next steps">
            <h4 className="nb-context-steps-head">Next steps</h4>
            {item.entryType === 'link' && (
              <a className="nb-context-step" href={item.url} target="_blank" rel="noreferrer noopener">
                {INTENT_VERB[item.intent] === 'Open' ? 'Open' : `Open ${INTENT_VERB[item.intent]}`} ↗
              </a>
            )}
            <button
              type="button"
              className="nb-context-step"
              data-test="nb-context-done"
              disabled={busy}
              onClick={() => onDone(item)}
            >
              {item.status === 'done' ? 'Mark not done' : `Mark ${doneVerb(item.kind)}`}
            </button>
            {/* Audience is stated ONCE, in the meta line above (founder email
                2026-10-23: "just me is listed twice — doesn't need to be a
                pill"). The card face keeps the VisibilityPill as the control;
                the panel is the read face (PRD §6.4). */}
            {isOwner && (
              <button type="button" className="nb-context-step" data-test="nb-context-edit" disabled={busy} onClick={() => onEdit(item)}>
                Edit
              </button>
            )}
            {isOwner && (
              <button type="button" className="nb-context-step nb-context-step-quiet" data-test="nb-context-remove" disabled={busy} onClick={() => onRemove(item)}>
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
